export type ComponentValue =
  | string
  | number
  | boolean
  | string[]
  | { [key: string]: ComponentValue };

export type DeviceComponent = () => Promise<Record<string, ComponentValue> | null>;

const GPU_ADAPTER_TIMEOUT_MS = 300;

// The library rejects null/undefined leaves, and an absent API must look absent, not "undefined".
const compact = (value: Record<string, unknown>): Record<string, ComponentValue> =>
  Object.fromEntries(
    Object.entries(value).filter(
      ([, v]) => v !== undefined && v !== null && !(typeof v === 'number' && Number.isNaN(v)),
    ),
  ) as Record<string, ComponentValue>;

// A component is diagnostics, never a reason to lose the rest: any throw becomes null.
const safe = (collect: () => Promise<Record<string, unknown> | null>): DeviceComponent =>
  async () => {
    try {
      const value = await collect();
      return value ? compact(value) : null;
    } catch {
      return null;
    }
  };

const matches = (query: string): boolean => {
  try {
    return window.matchMedia?.(query).matches ?? false;
  } catch {
    return false;
  }
};

const firstMatching = (feature: string, values: string[], fallback: string): string =>
  values.find((v) => matches(`(${feature}: ${v})`)) ?? fallback;

const uach = safe(async () => {
  const data = (navigator as any).userAgentData;
  if (!data?.getHighEntropyValues) return null;
  const hints = await data.getHighEntropyValues([
    'model',
    'platformVersion',
    'architecture',
    'bitness',
    'formFactors',
    'fullVersionList',
  ]);
  return {
    platform: data.platform,
    mobile: data.mobile,
    model: hints.model,
    platformVersion: hints.platformVersion,
    architecture: hints.architecture,
    bitness: hints.bitness,
    formFactors: hints.formFactors,
    fullVersionList: Array.isArray(hints.fullVersionList)
      ? hints.fullVersionList.map(
          (b: { brand: string; version: string }) => `${b.brand}/${b.version}`,
        )
      : undefined,
  };
});

const display = safe(async () => {
  const { width, height, colorDepth } = window.screen;
  return {
    long: Math.max(width, height),
    short: Math.min(width, height),
    dpr: Math.round((window.devicePixelRatio || 0) * 100) / 100,
    colorDepth,
    gamut: firstMatching('color-gamut', ['rec2020', 'p3', 'srgb'], 'none'),
    hdr: matches('(dynamic-range: high)'),
    pointer: firstMatching('pointer', ['fine', 'coarse', 'none'], 'unknown'),
    hover: matches('(hover: hover)'),
  };
});

const cpu = safe(async () => ({
  hardwareConcurrency: navigator.hardwareConcurrency,
  deviceMemory: (navigator as any).deviceMemory,
  maxTouchPoints: navigator.maxTouchPoints,
}));

const glparams = safe(async () => {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
  if (!gl) return null;
  try {
    const precision = (shader: number, type: number) => {
      const f = gl.getShaderPrecisionFormat(shader, type);
      return f ? `${f.rangeMin},${f.rangeMax},${f.precision}` : 'none';
    };
    const viewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS);
    return {
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
      maxViewportDims: viewport ? Array.from(viewport as Int32Array).map(String) : undefined,
      vertexHighFloat: precision(gl.VERTEX_SHADER, gl.HIGH_FLOAT),
      fragmentHighFloat: precision(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT),
      extensions: (gl.getSupportedExtensions() ?? []).slice().sort(),
    };
  } finally {
    // Browsers cap live WebGL contexts; release ours instead of waiting for GC.
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
});

const gpu = safe(async () => {
  const api = (navigator as any).gpu;
  if (!api?.requestAdapter) return null;
  const timeout = new Promise<null>((resolve) =>
    setTimeout(() => resolve(null), GPU_ADAPTER_TIMEOUT_MS),
  );
  const adapter = await Promise.race([api.requestAdapter(), timeout]);
  if (!adapter) return null;
  const info = adapter.info ?? (await adapter.requestAdapterInfo?.());
  return info ? { vendor: info.vendor, architecture: info.architecture } : null;
});

const intl = safe(async () => {
  const options = Intl.DateTimeFormat().resolvedOptions();
  return {
    timeZone: options.timeZone,
    calendar: options.calendar,
    numberingSystem: options.numberingSystem,
    hourCycle: (options as any).hourCycle,
    languages: Array.from(navigator.languages ?? []),
    timezoneOffset: new Date().getTimezoneOffset(),
  };
});

const automation = safe(async () => ({
  webdriver: navigator.webdriver === true,
  headlessUa: /HeadlessChrome/.test(navigator.userAgent),
  zeroOuterWindow: window.outerWidth === 0 || window.outerHeight === 0,
}));

const GIB = 1024 ** 3;

const storage = safe(async () => {
  const estimate = await navigator.storage?.estimate?.();
  if (!estimate?.quota) return null;
  return { quotaGiB: Math.round(estimate.quota / GIB) };
});

export const CUSTOM_COMPONENTS: Record<string, DeviceComponent> = {
  uach,
  display,
  cpu,
  glparams,
  gpu,
  intl,
  automation,
  storage,
};
